import { useCallback, useEffect, useState } from "react";
import { apiCall, ApiError } from "@/lib/api";

export type DashboardPeriod = "7d" | "30d" | "90d" | "all";
export type DashboardView = "executive" | "manager";

export interface DashboardScope {
  projectId: string | null;
  cohortName: string | null;
  cohortId: string | null;
}

export interface ExecutiveData {
  client: { id: string; name: string };
  scope?: DashboardScope;
  period: DashboardPeriod;
  totals: { learners: number; cohorts: number; sims: number; freshness: string | null };
  readiness: { strong: number; developing: number; needsWork: number; total: number };
  competencies: Array<{ name: string; avgScore: number | null; n: number }>;
  topPerformers: Array<{ userId: string; fullName: string; cohortName: string; avgScore: number | null; n: number }>;
  standoutCohort: { cohortName: string; avgScore: number | null; n: number } | null;
  concernCohort: { cohortName: string; avgScore: number | null; n: number } | null;
  uplift: { firstScore: number | null; latestScore: number | null; delta: number | null; n: number };
  themes: { insights: unknown; generatedAt: string } | null;
}

export interface ManagerData {
  client: { id: string; name: string };
  scope?: DashboardScope;
  period: DashboardPeriod;
  funnel: { assigned: number; started: number; inProgress: number; completed: number; notStarted: number };
  actionQueues: {
    notStarted: { total: number; rows: Array<{ userId: string; fullName: string; cohortName: string; assignedAt: string }> };
    stuck: { total: number; rows: Array<{ userId: string; fullName: string; cohortName: string; lastActivity: string; idleDays: number }> };
    atRisk: { total: number; rows: Array<{ userId: string; fullName: string; cohortName: string; avgScore: number | null; simCount: number }> };
  };
  completionBySim: Array<{ simulationId: string; simTitle: string; assigned: number; completed: number; completionPct: number }>;
  timeToComplete: Array<{ simulationId: string; simTitle: string; medianMinutes: number | null; n: number }>;
  dropOffMap: Array<{ simulationId: string; simTitle: string; maxSequence: number | null; reached: Array<{ seq: number; n: number }> }>;
}

interface UseDashboardState<T> {
  loading: boolean;
  error: string | null;
  data: T | null;
  reload: () => void;
}

function toMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Failed to load";
}

/** Sentinel from DashboardPage — means "server, resolve my client from auth". */
const CLIENT_ADMIN_SENTINEL = "__client_admin_self__";

function buildBody(
  clientId: string, period: DashboardPeriod,
  projectId: string | null, cohortId: string | null,
): Record<string, unknown> {
  const base: Record<string, unknown> = clientId === CLIENT_ADMIN_SENTINEL
    ? { period }
    : { clientId, period };
  if (projectId !== null && projectId !== "") base["projectId"] = projectId;
  if (cohortId !== null && cohortId !== "") base["cohortId"] = cohortId;
  return base;
}

export function useExecutiveDashboard(
  clientId: string | null, period: DashboardPeriod,
  projectId: string | null = null, cohortId: string | null = null,
): UseDashboardState<ExecutiveData> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ExecutiveData | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (clientId === null || clientId === "") { setData(null); return; }
    setLoading(true);
    setError(null);
    const call = (): Promise<ExecutiveData> =>
      apiCall<ExecutiveData>("/libraryDashboardExecutive", { method: "POST", body: buildBody(clientId, period, projectId, cohortId), timeoutMs: 120_000 });
    try {
      let result: ExecutiveData;
      try {
        result = await call();
      } catch (e) {
        const isAbort = e instanceof Error && /abort/i.test(e.message);
        if (!isAbort) throw e;
        await new Promise((r) => setTimeout(r, 1000));
        result = await call();
      }
      setData(result);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setLoading(false);
    }
  }, [clientId, period, projectId, cohortId]);

  useEffect(() => { void load(); }, [load]);

  return { loading, error, data, reload: () => void load() };
}

export function useManagerDashboard(
  clientId: string | null, period: DashboardPeriod,
  projectId: string | null = null, cohortId: string | null = null,
): UseDashboardState<ManagerData> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ManagerData | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (clientId === null || clientId === "") { setData(null); return; }
    setLoading(true);
    setError(null);
    const call = (): Promise<ManagerData> =>
      apiCall<ManagerData>("/libraryDashboardManager", { method: "POST", body: buildBody(clientId, period, projectId, cohortId), timeoutMs: 120_000 });
    try {
      let result: ManagerData;
      try {
        result = await call();
      } catch (e) {
        const isAbort = e instanceof Error && /abort/i.test(e.message);
        if (!isAbort) throw e;
        await new Promise((r) => setTimeout(r, 1000));
        result = await call();
      }
      setData(result);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setLoading(false);
    }
  }, [clientId, period, projectId, cohortId]);

  useEffect(() => { void load(); }, [load]);

  return { loading, error, data, reload: () => void load() };
}
